/**
 * Phase 0 billing — LemonSqueezy overlay checkout + credits API client.
 *
 * Checkout URLs are loaded from VITE_LS_CHECKOUT_* env vars at build
 * time. Until the user provisions their LS store + populates these vars
 * on the deploy host, `isBillingConfigured()` returns false and the
 * frontend falls back to a "Coming soon — set up payments first" toast
 * on Upgrade clicks. Free tier works end-to-end regardless.
 */

import { pb } from "@/lib/pb";
import type { CreditsAggregate, PlanTier } from "@/types";

export type BillingCadence = "monthly" | "annual";

/* ── Tier price + product map (mirror PHASE-0-DECISIONS.md) ──── */

export interface TierConfig {
  /** Tier identifier (matches `subscriptions.plan`). */
  id: PlanTier;
  /** User-facing label. */
  name: string;
  /** Short positioning blurb on the Plans card. */
  tagline: string;
  /** Pricing matrix in USD. */
  monthly: number;
  annual: number;
  /** Generation allotment per period. */
  credits: number;
  /** Display period label ("daily" / "monthly"). */
  cadence: "daily" | "monthly";
  /** Workspace + seat limits. */
  workspaces: number | "unlimited";
  seats: number;
  /** Feature checklist shown on the card. */
  features: string[];
  /** Highlight ribbon ("Most popular", "Best for agencies", etc.). */
  highlight?: string;
}

export const TIERS: TierConfig[] = [
  {
    id: "free",
    name: "Free",
    tagline: "Start exploring CanvaLoop. No card required.",
    monthly: 0,
    annual: 0,
    credits: 30,
    cadence: "monthly",
    workspaces: 1,
    seats: 1,
    features: [
      "30 generations / month",
      "5 / day daily cap",
      "1 workspace",
      "Full Visual Architect engine",
      "Brand-aware output",
      "Library + favourites",
    ],
  },
  {
    id: "pro",
    name: "Pro",
    tagline: "For brand owners shipping content weekly.",
    monthly: 29,
    annual: Math.round(29 * 12 * 0.8), // 20% off annual
    credits: 500,
    cadence: "monthly",
    workspaces: 3,
    seats: 1,
    features: [
      "500 generations / month",
      "3 workspaces",
      "Image editor (inpaint)",
      "Grid Row carousel slicer",
      "@brand element references",
      "Priority queue",
      "Buy +200 top-ups anytime",
    ],
    highlight: "Most popular",
  },
  {
    id: "studio",
    name: "Studio",
    tagline: "For agencies + teams shipping for multiple brands.",
    monthly: 99,
    annual: Math.round(99 * 12 * 0.75), // 25% off annual
    credits: 2500,
    cadence: "monthly",
    workspaces: "unlimited",
    seats: 3,
    features: [
      "2,500 generations / month (pooled)",
      "Unlimited workspaces",
      "3 seats baked in · +$9/seat",
      "Team roles + permissions",
      "Everything in Pro",
      "Priority + fast support",
      "Buy +200 top-ups anytime",
    ],
    highlight: "Best for agencies",
  },
];

export const TOP_UP_PACK = {
  credits: 200,
  priceUsd: 10,
};

/* ── LemonSqueezy checkout URLs (env-driven) ─────────────────── */

interface CheckoutUrls {
  proMonthly: string;
  proAnnual: string;
  studioMonthly: string;
  studioAnnual: string;
  topup200: string;
}

export function getCheckoutUrls(): CheckoutUrls {
  return {
    proMonthly:    (import.meta.env.VITE_LS_CHECKOUT_PRO_MONTHLY    as string) || "",
    proAnnual:     (import.meta.env.VITE_LS_CHECKOUT_PRO_ANNUAL     as string) || "",
    studioMonthly: (import.meta.env.VITE_LS_CHECKOUT_STUDIO_MONTHLY as string) || "",
    studioAnnual:  (import.meta.env.VITE_LS_CHECKOUT_STUDIO_ANNUAL  as string) || "",
    topup200:      (import.meta.env.VITE_LS_CHECKOUT_TOPUP_200      as string) || "",
  };
}

/** True if at least the Pro monthly checkout URL has been wired. */
export function isBillingConfigured(): boolean {
  const urls = getCheckoutUrls();
  return Boolean(urls.proMonthly);
}

export function getCheckoutUrl(plan: PlanTier, cadence: BillingCadence): string {
  const urls = getCheckoutUrls();
  if (plan === "pro")    return cadence === "annual" ? urls.proAnnual    : urls.proMonthly;
  if (plan === "studio") return cadence === "annual" ? urls.studioAnnual : urls.studioMonthly;
  return "";
}

export function getTopUpUrl(): string {
  return getCheckoutUrls().topup200;
}

/* ── lemon.js overlay loader ─────────────────────────────────── */

declare global {
  interface Window {
    LemonSqueezy?: {
      Setup: (cfg: { eventHandler?: (e: { event: string; data?: unknown }) => void }) => void;
      Url: { Open: (url: string) => void; Close: () => void };
      Refresh: () => void;
    };
    createLemonSqueezy?: () => void;
  }
}

let _lemonJsPromise: Promise<void> | null = null;

/** Lazy-load the LemonSqueezy `lemon.js` script. Idempotent. */
function loadLemonJs(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.LemonSqueezy) return Promise.resolve();
  if (_lemonJsPromise) return _lemonJsPromise;
  _lemonJsPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>("script[data-cl-lemonjs]");
    if (existing) { existing.addEventListener("load", () => resolve()); return; }
    const s = document.createElement("script");
    s.src = "https://assets.lemonsqueezy.com/lemon.js";
    s.defer = true;
    s.setAttribute("data-cl-lemonjs", "1");
    s.onload  = () => resolve();
    s.onerror = () => reject(new Error("Failed to load LemonSqueezy script"));
    document.head.appendChild(s);
  });
  return _lemonJsPromise;
}

/**
 * Open the LemonSqueezy in-page overlay checkout.
 * Appends the authenticated user's id as custom data so the webhook
 * handler can map the LS subscription back to our PB user.
 */
export async function openCheckout(
  url: string,
  opts: { userId: string; email?: string; onSuccess?: () => void } = { userId: "" },
): Promise<void> {
  if (!url) throw new Error("Checkout URL not configured");
  if (typeof window === "undefined") return;

  const enriched = appendCustomData(url, {
    userId: opts.userId,
    ...(opts.email ? { email: opts.email } : {}),
  });

  await loadLemonJs();
  if (!window.LemonSqueezy) {
    // Fallback: open in new tab.
    window.open(enriched, "_blank", "noopener,noreferrer");
    return;
  }
  window.LemonSqueezy.Setup({
    eventHandler: (e) => {
      if (e?.event === "Checkout.Success") {
        opts.onSuccess?.();
      }
    },
  });
  window.LemonSqueezy.Url.Open(enriched);
}

function appendCustomData(url: string, custom: Record<string, string>): string {
  try {
    const u = new URL(url);
    for (const [k, v] of Object.entries(custom)) {
      if (v) u.searchParams.set(`checkout[custom][${k}]`, v);
    }
    // Prefill email if we have it (improves UX, LS supports this).
    if (custom.email) u.searchParams.set("checkout[email]", custom.email);
    return u.toString();
  } catch {
    return url;
  }
}

/* ── Credits API client ──────────────────────────────────────── */

export async function fetchCredits(): Promise<CreditsAggregate> {
  return pb.send<CreditsAggregate>("/api/cl/credits/me", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: "{}",
  });
}

/**
 * Heuristic 402 detector — PocketBase's ClientResponseError exposes the
 * status on `.status`. The generate hook returns 402 with structured
 * data so the frontend can decide between upgrade and top-up modals.
 */
export interface CreditExhaustedError {
  status: 402;
  message: string;
  data: {
    error: "credit_exhausted" | "monthly_exhausted";
    plan: PlanTier;
    remaining: number;
    upgrade?: boolean;
    topup?: boolean;
  };
}

export function isCreditExhausted(err: unknown): err is CreditExhaustedError {
  if (!err || typeof err !== "object") return false;
  const e = err as { status?: number; data?: { error?: string } };
  return e.status === 402 && (
    e.data?.error === "credit_exhausted" ||
    e.data?.error === "monthly_exhausted"
  );
}

/* ── Cost translation (Higgsfield pattern) ───────────────────── */

/**
 * Translate a credit count into a friendly "≈ N images" string for the
 * Plans page + credit pill hover popover. Today 1 credit = 1 generation,
 * so this is identity — but the helper lets us evolve the unit later
 * without rewriting copy sites everywhere.
 */
export function creditsAsImagesLabel(credits: number): string {
  if (credits <= 0) return "0 generations";
  if (credits === 1) return "1 generation";
  return `${credits.toLocaleString()} generations`;
}
